// src/utils/mockDataGRN.ts
// Step 3 — Goods Received Note (GRN) recording
// Step 4 — Formal 3-way matching: PO ↔ GRN ↔ Invoice

// ── Types ──────────────────────────────────────────────────────────

export type GRNStatus = 'DRAFT' | 'SUBMITTED' | 'VERIFIED'

export type GRNItemCondition = 'GOOD' | 'DAMAGED' | 'PARTIAL' | 'REJECTED'

export type MatchStatus =
  | 'FULL_MATCH'        // all three documents agree within tolerance
  | 'PRICE_VARIANCE'    // qty match, but invoice price differs from PO price
  | 'QUANTITY_VARIANCE' // invoice qty differs from GRN received qty
  | 'MISSING_GRN'       // no GRN recorded yet — cannot run match
  | 'MISSING_PO'        // no PO linked — cannot run match
  | 'DISPUTED'          // variance flagged and under resolution
  | 'APPROVED'          // PM approved despite variance (credit note pending etc.)

// ── Display metadata ───────────────────────────────────────────────

export const GRN_STATUS_META: Record<GRNStatus, { label: string; bg: string; text: string }> = {
  DRAFT:     { label: 'Draft',     bg: 'bg-gray-100',   text: 'text-gray-600'  },
  SUBMITTED: { label: 'Submitted', bg: 'bg-blue-100',   text: 'text-blue-700'  },
  VERIFIED:  { label: 'Verified',  bg: 'bg-green-100',  text: 'text-green-700' },
}

export const MATCH_STATUS_META: Record<MatchStatus, {
  label: string; bg: string; text: string; border: string; icon: string
}> = {
  FULL_MATCH:        { label: 'Full match',        bg: 'bg-green-50',  text: 'text-green-800',  border: 'border-green-200',  icon: '✓' },
  PRICE_VARIANCE:    { label: 'Price variance',    bg: 'bg-amber-50',  text: 'text-amber-800',  border: 'border-amber-300',  icon: '⚠' },
  QUANTITY_VARIANCE: { label: 'Quantity variance', bg: 'bg-red-50',    text: 'text-red-800',    border: 'border-red-300',    icon: '⚑' },
  MISSING_GRN:       { label: 'GRN not recorded',  bg: 'bg-gray-50',   text: 'text-gray-600',   border: 'border-gray-300',   icon: '○' },
  MISSING_PO:        { label: 'No PO linked',      bg: 'bg-gray-50',   text: 'text-gray-600',   border: 'border-gray-300',   icon: '○' },
  DISPUTED:          { label: 'Disputed',          bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-300', icon: '⚑' },
  APPROVED:          { label: 'Approved',          bg: 'bg-sky-50',    text: 'text-sky-800',    border: 'border-sky-200',    icon: '✓' },
}

// ── Interfaces ─────────────────────────────────────────────────────

export interface GRNLineItem {
  id: string
  description: string
  supplierPartNo?: string
  orderedQty: number
  receivedQty: number
  unit: string
  unitValue: number          // PO unit price (for value calc)
  condition: GRNItemCondition
  notes?: string
}

export interface GoodsReceivedNote {
  id: string
  grnNumber: string          // GRN-TP001-001
  poRef: string              // matches PO poNumber field
  jobId: string
  supplierId: string
  supplierName: string
  deliveryDate: string
  deliveryDocketRef: string  // supplier's delivery docket number
  deliveryLocation: string
  receivedById: string
  receivedByName: string
  status: GRNStatus
  lineItems: GRNLineItem[]
  totalOrderedValue: number
  totalReceivedValue: number
  hasDiscrepancy: boolean    // any qty shortfall or damage noted
  photoCount: number
  notes?: string
  verifiedById?: string
  verifiedByName?: string
  verifiedAt?: string
  createdAt: string
  updatedAt: string
}

export interface ThreeWayMatchResult {
  id: string
  invoiceId: string
  invoiceNumber: string
  poRef?: string
  grnId?: string
  grnNumber?: string
  matchStatus: MatchStatus
  // PO values
  poDescription?: string
  poOrderedQty?: number
  poUnitPrice?: number
  poTotal?: number
  poDate?: string
  // GRN values
  grnReceivedQty?: number
  grnReceivedValue?: number
  grnDate?: string
  grnReceivedBy?: string
  // Invoice values
  invDescription?: string
  invQty: number
  invUnitPrice: number
  invTotal: number
  invDate: string
  // Variances (positive = invoice claims more than PO/GRN)
  qtyVariance?: number       // invQty - grnReceivedQty
  priceVariancePct?: number  // (invUnitPrice - poUnitPrice) / poUnitPrice * 100
  valueVariance?: number     // invTotal - grnReceivedValue
  varianceDescription?: string
  // Resolution
  disputeNote?: string
  resolvedById?: string
  resolvedByName?: string
  resolvedAt?: string
  autoMatchConfidencePct?: number
  matchedAt?: string
}

// ── GRN Records ────────────────────────────────────────────────────

export const MOCK_GRNS: GoodsReceivedNote[] = [

  // GRN-TP001-001 — Olex cable delivery (matches PO-TP001-004)
  {
    id: 'grn-001',
    grnNumber: 'GRN-TP001-001',
    poRef: 'PO-TP001-004',
    jobId: 'j1',
    supplierId: 'sup-olex',
    supplierName: 'Prysmian Group Australia (Olex)',
    deliveryDate: '2026-06-16',
    deliveryDocketRef: 'OLX-DEL-2026-441-A',
    deliveryLocation: 'Sandgate Rd site — gate 2, laydown area',
    receivedById: 'u4',
    receivedByName: 'Sarah Lee',
    status: 'VERIFIED',
    lineItems: [
      {
        id: 'gli-001-1',
        description: '3C+E 6mm² Cu/XLPE/PVC power cable — Batch OX-24-168',
        supplierPartNo: 'OLX-3CE-6MM',
        orderedQty: 850,
        receivedQty: 850,
        unit: 'm',
        unitValue: 16.80,
        condition: 'GOOD',
        notes: 'Cable drums in good condition. Batch labels photographed.',
      },
    ],
    totalOrderedValue: 14_280,
    totalReceivedValue: 14_280,
    hasDiscrepancy: false,
    photoCount: 3,
    notes: 'Full delivery. Drums stored in locked site compound. Serial: OX-24-168, OX-24-169.',
    verifiedById: 'u2',
    verifiedByName: 'Jane Smith',
    verifiedAt: '2026-06-17T09:00:00Z',
    createdAt: '2026-06-16T15:30:00Z',
    updatedAt: '2026-06-17T09:00:00Z',
  },

  // GRN-TP001-002 — Aldridge signal heads + controller (matches PO-TP001-006)
  // KEY DISCREPANCY: Invoice will claim 26 heads; GRN records only 24 received
  {
    id: 'grn-002',
    grnNumber: 'GRN-TP001-002',
    poRef: 'PO-TP001-006',
    jobId: 'j1',
    supplierId: 'sup-aldridge',
    supplierName: 'Aldridge Traffic Architects',
    deliveryDate: '2026-06-07',
    deliveryDocketRef: 'ATA-DEL-2026-0388',
    deliveryLocation: 'Sandgate Rd site — signal head storage shed',
    receivedById: 'u5',
    receivedByName: 'Tony Rivera',
    status: 'VERIFIED',
    lineItems: [
      {
        id: 'gli-002-1',
        description: 'LED signal head 3-aspect 300mm (TMR TAER)',
        supplierPartNo: 'ATA-SIG-LED3',
        orderedQty: 24,
        receivedQty: 24,
        unit: 'each',
        unitValue: 680,
        condition: 'GOOD',
        notes: '24 signal heads received and counted. Delivery docket ATA-DEL-2026-0388 lists 24 units. No additional units delivered.',
      },
      {
        id: 'gli-002-2',
        description: 'R900S SCATS controller cabinet (Aldridge / Surtec)',
        supplierPartNo: 'ATA-CTRL-R900S',
        orderedQty: 1,
        receivedQty: 1,
        unit: 'each',
        unitValue: 14_800,
        condition: 'GOOD',
        notes: 'Cabinet serial ENX-R900-24-0419. Factory seal intact. Stored in locked compound.',
      },
    ],
    totalOrderedValue: 16_320 + 14_800,
    totalReceivedValue: 16_320 + 14_800,
    hasDiscrepancy: false,
    photoCount: 5,
    notes: 'Only 24 signal heads delivered per docket. Aldridge invoice to be checked — PO was for 24 units only.',
    verifiedById: 'u2',
    verifiedByName: 'Jane Smith',
    verifiedAt: '2026-06-08T10:00:00Z',
    createdAt: '2026-06-07T16:00:00Z',
    updatedAt: '2026-06-08T10:00:00Z',
  },

  // GRN-TP001-003 — Boral concrete delivery (matches PO-TP001-001)
  {
    id: 'grn-003',
    grnNumber: 'GRN-TP001-003',
    poRef: 'PO-TP001-001',
    jobId: 'j1',
    supplierId: 'sup-boral',
    supplierName: 'Boral Construction Materials',
    deliveryDate: '2026-06-18',
    deliveryDocketRef: 'BCQ-2026-441',
    deliveryLocation: 'Sandgate Rd — NW corner foundation excavation',
    receivedById: 'u6',
    receivedByName: 'Mike Walsh',
    status: 'SUBMITTED',
    lineItems: [
      {
        id: 'gli-003-1',
        description: 'Ready-mix concrete Grade 40 20mm aggregate',
        supplierPartNo: 'BRL-CONC-G40-20',
        orderedQty: 0.5,
        receivedQty: 0.5,
        unit: 'm³',
        unitValue: 248,
        condition: 'GOOD',
        notes: 'Slump test: 82mm (within 70–100mm spec). Batch BCQ-2026-441. Truck no. BRL-022.',
      },
    ],
    totalOrderedValue: 124,
    totalReceivedValue: 124,
    hasDiscrepancy: false,
    photoCount: 2,
    notes: 'Concrete batch test record photographed. NATA cert to be received within 28 days.',
    createdAt: '2026-06-18T11:30:00Z',
    updatedAt: '2026-06-18T11:30:00Z',
  },

  // GRN-TP003-001 — Saferoads civil materials (matches PO-TP003-002)
  {
    id: 'grn-004',
    grnNumber: 'GRN-TP003-001',
    poRef: 'PO-TP003-002',
    jobId: 'j3',
    supplierId: 'sup-saferoads',
    supplierName: 'Saferoads Queensland',
    deliveryDate: '2026-06-02',
    deliveryDocketRef: 'SRD-DEL-2026-0488',
    deliveryLocation: 'Maroochydore Rd site — materials laydown',
    receivedById: 'u4',
    receivedByName: 'Sarah Lee',
    status: 'VERIFIED',
    lineItems: [
      {
        id: 'gli-004-1',
        description: 'DIN base plate (Type F-2) for signal poles',
        supplierPartNo: 'SRD-DIN-BASE-F2',
        orderedQty: 6,
        receivedQty: 6,
        unit: 'each',
        unitValue: 420,
        condition: 'GOOD',
      },
      {
        id: 'gli-004-2',
        description: 'Y16 reinforcing steel — signal pole cages',
        supplierPartNo: 'SRD-REBAR-Y16',
        orderedQty: 0.2,
        receivedQty: 0.2,
        unit: 'tonne',
        unitValue: 1_280,
        condition: 'GOOD',
        notes: 'Mill cert for batch SRD-2026-Y16-0488 received. Filed.',
      },
      {
        id: 'gli-004-3',
        description: 'UPVC 100mm conduit 6m lengths (90 lengths = 540m)',
        supplierPartNo: 'SRD-UPVC-100',
        orderedQty: 540,
        receivedQty: 540,
        unit: 'm',
        unitValue: 6.40,
        condition: 'GOOD',
      },
    ],
    totalOrderedValue: 6_232,
    totalReceivedValue: 6_232,
    hasDiscrepancy: false,
    photoCount: 4,
    verifiedById: 'u2',
    verifiedByName: 'Jane Smith',
    verifiedAt: '2026-06-03T09:00:00Z',
    createdAt: '2026-06-02T14:00:00Z',
    updatedAt: '2026-06-03T09:00:00Z',
  },
]

// ── 3-Way Match Results ────────────────────────────────────────────

export const MOCK_MATCH_RESULTS: ThreeWayMatchResult[] = [

  // inv-001 (Boral concrete) ↔ PO-TP001-001 ↔ GRN-TP001-003 — FULL MATCH
  {
    id: 'match-001',
    invoiceId: 'inv-001',
    invoiceNumber: 'BCQ-INV-2026-0441',
    poRef: 'PO-TP001-001',
    grnId: 'grn-003',
    grnNumber: 'GRN-TP001-003',
    matchStatus: 'FULL_MATCH',
    poDescription: 'Ready-mix concrete Grade 40 20mm — foundation pour',
    poOrderedQty: 0.5, poUnitPrice: 248, poTotal: 124, poDate: '2026-06-10',
    grnReceivedQty: 0.5, grnReceivedValue: 124, grnDate: '2026-06-18', grnReceivedBy: 'Mike Walsh',
    invDescription: 'Ready-mix concrete Grade 40 20mm — delivery BCQ-2026-441',
    invQty: 0.5, invUnitPrice: 248, invTotal: 124, invDate: '2026-06-18',
    qtyVariance: 0, priceVariancePct: 0, valueVariance: 0,
    autoMatchConfidencePct: 100,
    matchedAt: '2026-06-19T10:00:00Z',
  },

  // inv-002 (Olex cable) ↔ PO-TP001-004 ↔ GRN-TP001-001 — PRICE VARIANCE
  // Quantities all match 850m. But PO was raised at $14.90/m; invoice is $16.80/m (copper surcharge)
  {
    id: 'match-002',
    invoiceId: 'inv-002',
    invoiceNumber: 'INV-OLX-2026-0441',
    poRef: 'PO-TP001-004',
    grnId: 'grn-001',
    grnNumber: 'GRN-TP001-001',
    matchStatus: 'PRICE_VARIANCE',
    poDescription: '3C+E 6mm² Cu/XLPE/PVC cable — per BOM circuit C1–C12',
    poOrderedQty: 850, poUnitPrice: 14.90, poTotal: 12_665, poDate: '2025-12-15',
    grnReceivedQty: 850, grnReceivedValue: 12_665, grnDate: '2026-06-16', grnReceivedBy: 'Sarah Lee',
    invDescription: '3C+E 6mm² Cu/XLPE/PVC cable — Batch OX-24-168, OX-24-169',
    invQty: 850, invUnitPrice: 16.80, invTotal: 14_280, invDate: '2026-06-15',
    qtyVariance: 0,
    priceVariancePct: 12.8,
    valueVariance: 1_615,
    varianceDescription: 'Invoice unit price ($16.80/m) is $1.90/m above PO price ($14.90/m). Supplier applied LME copper surcharge. Catalogue updated. Additional cost: $1,615.00 ex GST.',
    autoMatchConfidencePct: 95,
    matchedAt: '2026-06-16T09:00:00Z',
  },

  // inv-003 (Aldridge signal heads) ↔ PO-TP001-006 ↔ GRN-TP001-002 — QUANTITY VARIANCE
  // GRN: 24 heads received. Invoice: 26 heads billed. 2 extra not delivered.
  {
    id: 'match-003',
    invoiceId: 'inv-003',
    invoiceNumber: 'ALT-INV-2026-0388',
    poRef: 'PO-TP001-006',
    grnId: 'grn-002',
    grnNumber: 'GRN-TP001-002',
    matchStatus: 'QUANTITY_VARIANCE',
    poDescription: 'LED signal head 3-aspect 300mm (TMR TAER) × 24 + R900S controller × 1',
    poOrderedQty: 24, poUnitPrice: 680, poTotal: 31_120, poDate: '2026-05-20',
    grnReceivedQty: 24, grnReceivedValue: 16_320, grnDate: '2026-06-07', grnReceivedBy: 'Tony Rivera',
    invDescription: 'LED signal head 3-aspect 300mm (TMR TAER) — as per ATA-DEL-2026-0388',
    invQty: 26, invUnitPrice: 680, invTotal: 17_680, invDate: '2026-06-05',
    qtyVariance: 2,       // 26 invoiced - 24 received = 2 extra billed
    priceVariancePct: 0,
    valueVariance: 1_360, // 2 × $680 = $1,360 overbilled
    varianceDescription: 'Invoice claims 26 signal heads. GRN records only 24 received (matching PO). 2 units billed but not delivered. Potential overpayment: $1,360.00 ex GST. Credit note requested from Aldridge on 7 Jun 2026.',
    disputeNote: 'Aldridge contacted 7 Jun 2026. Credit note ATA-CN-2026-041 for $1,360 ex GST expected within 14 days.',
    autoMatchConfidencePct: 88,
    matchedAt: '2026-06-08T10:00:00Z',
  },

  // inv-004 (Blackwoods PPE) — no PO and no GRN — MISSING_PO
  {
    id: 'match-004',
    invoiceId: 'inv-004',
    invoiceNumber: 'BWD-INV-2026-0419',
    matchStatus: 'MISSING_PO',
    invDescription: 'PPE items — steel cap boots × 2 pair, hi-vis vest × 4, cut-resistant gloves × 2 pack',
    invQty: 1, invUnitPrice: 604, invTotal: 604, invDate: '2026-05-10',
    varianceDescription: 'No purchase order was raised before these goods were ordered. The match cannot proceed without a PO. Raise a PO retrospectively and link it before approving payment.',
    autoMatchConfidencePct: 0,
    matchedAt: '2026-06-20T14:00:00Z',
  },

  // inv-005 (Saferoads civil) ↔ PO-TP003-002 ↔ GRN-TP003-001 — PRICE VARIANCE (favourable — steel price fell)
  {
    id: 'match-005',
    invoiceId: 'inv-005',
    invoiceNumber: 'SRD-INV-2026-0488',
    poRef: 'PO-TP003-002',
    grnId: 'grn-004',
    grnNumber: 'GRN-TP003-001',
    matchStatus: 'PRICE_VARIANCE',
    poDescription: 'Y16 reo, DIN plates × 6, UPVC 100mm conduit 540m',
    poOrderedQty: 1, poUnitPrice: 6_232, poTotal: 6_232, poDate: '2026-04-20',
    grnReceivedQty: 1, grnReceivedValue: 6_232, grnDate: '2026-06-02', grnReceivedBy: 'Sarah Lee',
    invDescription: 'Y16 reinforcing steel, DIN base plates × 6, UPVC 100mm conduit',
    invQty: 1, invUnitPrice: 6_076, invTotal: 6_076, invDate: '2026-06-01',
    qtyVariance: 0,
    priceVariancePct: -2.5,
    valueVariance: -156,  // negative = invoice is LESS than PO (favourable)
    varianceDescription: 'Invoice total ($6,076) is $156 below PO value ($6,232). Reo steel price fell from $1,440/t to $1,280/t (market correction). Favourable variance — we pay less than PO. No action required but PO and catalogue should be updated.',
    autoMatchConfidencePct: 96,
    matchedAt: '2026-06-02T15:00:00Z',
  },
]


// ──────────────────────────────────────────────────────────────────
// CANONICAL STOCK STATUS + DELIVERY CONFIRMATION + INVOICE-PO LINK
// Shared stores read by PurchaseOrders, Drawings, MobileComms, Roster
// ──────────────────────────────────────────────────────────────────

export type StockStatus = 'NOT_ORDERED' | 'ORDERED' | 'IN_TRANSIT' | 'IN_WAREHOUSE' | 'ON_SITE' | 'PARTIAL' | 'NOT_AVAILABLE'

export const STOCK_STATUS_META: Record<StockStatus, { label:string; bg:string; text:string; border:string; icon:string; description:string }> = {
  NOT_ORDERED:  { label:'Not ordered',   bg:'bg-gray-100',   text:'text-gray-600',   border:'border-gray-200',   icon:'\u25cb', description:'No PO raised yet'                    },
  ORDERED:      { label:'On order',      bg:'bg-amber-100',  text:'text-amber-700',  border:'border-amber-300',  icon:'\ud83d\udccb', description:'PO sent, awaiting delivery'          },
  IN_TRANSIT:   { label:'In transit',    bg:'bg-purple-100', text:'text-purple-700', border:'border-purple-300', icon:'\ud83d\ude9a', description:'Dispatched, expected on site'         },
  IN_WAREHOUSE: { label:'At warehouse',  bg:'bg-blue-100',   text:'text-blue-700',   border:'border-blue-300',   icon:'\ud83d\udce6', description:'Received at depot, not yet on site'  },
  ON_SITE:      { label:'On site',       bg:'bg-green-100',  text:'text-green-700',  border:'border-green-300',  icon:'\u2713',      description:'Available at job site'               },
  PARTIAL:      { label:'Partial',       bg:'bg-orange-100', text:'text-orange-700', border:'border-orange-300', icon:'\u26a0',      description:'Partial delivery, remainder pending' },
  NOT_AVAILABLE:{ label:'Not available', bg:'bg-red-100',    text:'text-red-700',    border:'border-red-300',    icon:'\u2715',      description:'Out of stock or delivery delayed'     },
}

export type DeliveryLineStatus = 'PENDING' | 'RECEIVED' | 'PARTIAL' | 'REJECTED' | 'DAMAGED'
export type DeliveryLocation   = 'IN_WAREHOUSE' | 'ON_SITE' | 'WORKSHOP' | 'OTHER'

export interface DeliveryChecklistLine {
  lineId: string; description: string; unit: string
  orderedQty: number; receivedQty: number
  status: DeliveryLineStatus; condition: GRNItemCondition
  deliveryLocation: DeliveryLocation
  bomLineId: string | null; supplierPartNo: string | null; notes: string
}

export interface DeliveryConfirmation {
  id: string; poRef: string; jobId: string; jobNumber: string
  supplierId: string; supplierName: string
  confirmedAt: string; confirmedByName: string; deliveryDocketRef: string
  lines: DeliveryChecklistLine[]; allReceived: boolean; hasDiscrepancy: boolean; notes: string
}

export interface InvoicePOLink {
  invoiceId: string; invoiceNumber: string; poRef: string
  linkedAt: string; linkedByName: string; note: string
}

const _invoicePOLinks:        InvoicePOLink[]          = []
const _deliveryConfirmations: DeliveryConfirmation[]   = []
const _bomStockOverrides:     Map<string, StockStatus> = new Map()

export function linkInvoiceToPO(invoiceId: string, invoiceNumber: string, poRef: string, linkedByName: string, note = ''): void {
  const i = _invoicePOLinks.findIndex(l => l.invoiceId === invoiceId)
  const lnk: InvoicePOLink = { invoiceId, invoiceNumber, poRef, linkedAt: new Date().toISOString(), linkedByName, note }
  if (i >= 0) _invoicePOLinks[i] = lnk; else _invoicePOLinks.push(lnk)
}
export function getLinkedPOForInvoice(invoiceId: string): string | null {
  return _invoicePOLinks.find(l => l.invoiceId === invoiceId)?.poRef ?? null
}
export function getAllInvoicePOLinks(): InvoicePOLink[] { return [..._invoicePOLinks] }

export function addDeliveryConfirmation(c: DeliveryConfirmation): void {
  const i = _deliveryConfirmations.findIndex(d => d.poRef === c.poRef)
  if (i >= 0) _deliveryConfirmations[i] = c; else _deliveryConfirmations.push(c)
  for (const line of c.lines) {
    if (!line.bomLineId || line.status === 'PENDING') continue
    const s: StockStatus =
      line.condition === 'REJECTED' ? 'NOT_AVAILABLE' :
      line.status === 'PARTIAL'     ? 'PARTIAL' :
      line.deliveryLocation === 'ON_SITE' ? 'ON_SITE' : 'IN_WAREHOUSE'
    _bomStockOverrides.set(line.bomLineId, s)
  }
}
export function getDeliveryConfirmations(): DeliveryConfirmation[] { return [..._deliveryConfirmations] }
export function getDeliveryConfirmationForPO(poRef: string): DeliveryConfirmation | undefined {
  return _deliveryConfirmations.find(c => c.poRef === poRef)
}
/** Get effective stock status: Warehouse Manager override » BOM seed value */
export function getEffectiveBOMStockStatus(bomLineId: string, seedStatus: string): string {
  return _bomStockOverrides.get(bomLineId) ?? seedStatus
}
export function isMaterialReady(status: string): boolean {
  return status === 'ON_SITE' || status === 'IN_WAREHOUSE'
}
export function getAllBOMStockOverrides(): Map<string, StockStatus> {
  return new Map(_bomStockOverrides)
}

// HP4 fix — direct stock override for warehouse confirmation
export function setBOMStockStatus(bomLineId: string, status: StockStatus): void {
  _bomStockOverrides.set(bomLineId, status)
}

// ── Helpers ───────────────────────────────────────────────────────

export function getGRNForPO(poRef: string): GoodsReceivedNote | undefined {
  return MOCK_GRNS.find(g => g.poRef === poRef)
}

export function getGRNById(id: string): GoodsReceivedNote | undefined {
  return MOCK_GRNS.find(g => g.id === id)
}

export function getMatchForInvoice(invoiceId: string): ThreeWayMatchResult | undefined {
  return MOCK_MATCH_RESULTS.find(m => m.invoiceId === invoiceId)
}

export function countVariances(): { qty: number; price: number; missing: number } {
  return {
    qty:     MOCK_MATCH_RESULTS.filter(m => m.matchStatus === 'QUANTITY_VARIANCE').length,
    price:   MOCK_MATCH_RESULTS.filter(m => m.matchStatus === 'PRICE_VARIANCE').length,
    missing: MOCK_MATCH_RESULTS.filter(m => ['MISSING_GRN','MISSING_PO'].includes(m.matchStatus)).length,
  }
}

export function poNeedsGRN(poStatus: string): boolean {
  return ['APPROVED','SENT','PARTIALLY_DELIVERED'].includes(poStatus)
}
